import React from 'react';
import { Button, Card, Stack, Typography, CircularProgress, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { usePaymentQueries } from '../../../hooks/usePaymentQueries';
import { useNotification } from '../../../context/NotificationContext';

const BillingPortalSection: React.FC = () => {
    const { t } = useTranslation();
    const { createBillingPortalSession } = usePaymentQueries();
    const { showNotification } = useNotification();
    
    const useCreateBillingPortalSession = () => {
        return useMutation({
            mutationFn: createBillingPortalSession,
        });
    };
    
    const billingPortalMutation = useCreateBillingPortalSession();

    const handleBillingPortalClick = async () => {
        try {
            const { url } = await billingPortalMutation.mutateAsync(undefined);
            if (url) {
                window.open(url, '_blank');
            }
        } catch (error: any) {
            console.error('Error opening billing portal:', error);
            if (error.response?.status === 404) {
                showNotification(t('settings.plan.errors.noBillingInfo'), 'error');
            } else {
                showNotification(t('settings.plan.errors.billingPortalError', { message: error.message }), 'error');
            }
        }
    };

    return (
        <Card sx={{ p: 3 }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                        {t('settings.billingHistory.title', 'Rechnungs- & Kaufverlauf')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('settings.billingHistory.description', 'Verwalte deine Rechnungen und Zahlungsinformationen im Kundenportal')}
                    </Typography>
                </Box>

                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={billingPortalMutation.isPending ? <CircularProgress size={16} /> : <ReceiptIcon />}
                        onClick={handleBillingPortalClick}
                        disabled={billingPortalMutation.isPending}
                        sx={{ 
                            textTransform: 'none',
                            height: '48px',
                            minWidth: '160px'
                        }}
                    >
                        {billingPortalMutation.isPending
                            ? t('common.loading', 'Wird geladen...')
                            : t('settings.billingHistory.viewInvoices', 'Rechnungen anzeigen')}
                    </Button>
                </Box>
            </Stack>
        </Card>
    );
};

export default BillingPortalSection;